/**
 * Los iconos del sistema.
 *
 * Dibujados a mano y no traídos de una librería: son doce, pesan menos que
 * cualquier paquete, y así todos comparten el mismo trazo —1,6 px, esquinas
 * redondeadas, rejilla de 24— en vez de mezclar tres estilos distintos.
 *
 * VAN DE CONTORNO Y NO MACIZOS. Sobre el morado del menú, un icono macizo es
 * una mancha; de contorno se lee como un símbolo y no compite con la palabra
 * que tiene al lado, que es la que de verdad se lee.
 *
 * Toman el color del texto (`currentColor`), así que el estado activo del
 * menú los tiñe solos sin una regla más.
 */

type Props = { className?: string; style?: React.CSSProperties }

function Base({ children, className, style }: Props & { children: React.ReactNode }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Cómo llegar · la flecha de navegación. */
export function IconoRuta(p: Props) {
  return (
    <Base {...p}>
      <path d="M3.5 11 20 4l-7 16.5-2.2-7.3L3.5 11Z" />
    </Base>
  )
}

/** Llamar · el auricular. */
export function IconoTelefono(p: Props) {
  return (
    <Base {...p}>
      <path d="M6.6 3.5h2.6l1.4 4-2 1.3a12 12 0 0 0 6.6 6.6l1.3-2 4 1.4v2.6a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.7a2 2 0 0 1 2-2.2Z" />
    </Base>
  )
}

/** Reportar avance · la bandera del tramo hecho. */
export function IconoAvance(p: Props) {
  return (
    <Base {...p}>
      <path d="M5 21V4" />
      <path d="M5 4h11l-2 3.5L16 11H5" />
    </Base>
  )
}

/** Almuerzo · la taza. */
export function IconoAlmuerzo(p: Props) {
  return (
    <Base {...p}>
      <path d="M4 9h12v4a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V9Z" />
      <path d="M16 10h1.5a2.5 2.5 0 0 1 0 5H16" />
      <path d="M8 3.5v2.5M12 3.5v2.5" />
      <path d="M3.5 21h13" />
    </Base>
  )
}

/** Tablero · los cuadros de un panel de control. */
export function IconoTablero(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </Base>
  )
}

/** Matriz de órdenes · columnas de un tablero de recorrido. */
export function IconoMatriz(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="4" width="5" height="16" rx="1.5" />
      <rect x="9.5" y="4" width="5" height="10" rx="1.5" />
      <rect x="16" y="4" width="5" height="13" rx="1.5" />
    </Base>
  )
}

/** Chatbot · la burbuja de WhatsApp. */
export function IconoChat(p: Props) {
  return (
    <Base {...p}>
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.3-.6L3 21l1.8-4.7A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4Z" />
    </Base>
  )
}

/** Herramientas · una llave inglesa. */
export function IconoHerramienta(p: Props) {
  return (
    <Base {...p}>
      <path d="M14.7 6.3a4 4 0 0 0 5.1 5.1l-8.4 8.4a2.4 2.4 0 0 1-3.4-3.4Z" />
      <path d="M14.7 6.3 17.5 3.5" />
      <path d="m5 19 1 1" />
    </Base>
  )
}

/** Mapa · un alfiler sobre el terreno. */
export function IconoMapa(p: Props) {
  return (
    <Base {...p}>
      <path d="M20 10c0 5.2-8 12-8 12s-8-6.8-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="2.8" />
    </Base>
  )
}

/** Archivo · una carpeta con lo que ya terminó. */
export function IconoArchivo(p: Props) {
  return (
    <Base {...p}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h9A1.5 1.5 0 0 1 21 10v8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18Z" />
      <path d="m9.5 14 2 2 3.5-4" />
    </Base>
  )
}

/** Clientes · dos personas. */
export function IconoClientes(p: Props) {
  return (
    <Base {...p}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16.5 5.4a3.2 3.2 0 0 1 0 5.2" />
      <path d="M18 14.6a6 6 0 0 1 3 5.4" />
    </Base>
  )
}

/** Inventario · cajas apiladas. */
export function IconoInventario(p: Props) {
  return (
    <Base {...p}>
      <path d="M3 8.5 12 4l9 4.5-9 4.5Z" />
      <path d="M3 8.5V16l9 4.5 9-4.5V8.5" />
      <path d="M12 13v7.5" />
    </Base>
  )
}

/** Inventario de sobrantes · la caja de vuelta, con su flecha de regreso. */
export function IconoSobrantes(p: Props) {
  return (
    <Base {...p}>
      <path d="M4 10.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8.5" />
      <path d="M3 7h18v3.5H3Z" />
      <path d="M14.5 15H9.5" />
      <path d="m11.5 13-2 2 2 2" />
    </Base>
  )
}

/** Cotizaciones · un documento con cifras. */
export function IconoCotizacion(p: Props) {
  return (
    <Base {...p}>
      <path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v4h4" />
      <path d="M8.5 13h7M8.5 16.5h4" />
    </Base>
  )
}

/** Facturas de venta · un recibo con su corte y el signo de pesos. Distinto
 *  de la hoja de Cotizaciones: en el menú iban con el mismo dibujo. */
export function IconoFactura(p: Props) {
  return (
    <Base {...p}>
      <path d="M6 3h12v18l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4L6 21V3Z" />
      <path d="M12 6.5v9" />
      <path d="M14.4 8.6c-.5-.6-1.3-.9-2.4-.9-1.4 0-2.4.6-2.4 1.6s1 1.4 2.4 1.7 2.4.7 2.4 1.7-1 1.7-2.4 1.7c-1.1 0-2-.3-2.5-1" />
    </Base>
  )
}

/** Cartera · una billetera con su pago. */
export function IconoCartera(p: Props) {
  return (
    <Base {...p}>
      <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H17v3" />
      <path d="M4 7.5V17a2 2 0 0 0 2 2h13a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H6.5A2.5 2.5 0 0 1 4 7.5Z" />
      <circle cx="15.5" cy="13.5" r="1.2" />
    </Base>
  )
}

/**
 * Alistamientos · la planilla con su visto bueno: la lista de lo que hay
 * que alistar. Era una caja y se confundía con el inventario (Johan:
 * «diferente svg»), que ya es una caja.
 */
export function IconoAlistamiento(p: Props) {
  return (
    <Base {...p}>
      <path d="M9 4.5H7a2 2 0 0 0-2 2V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6.5a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="3.2" rx="1" />
      <path d="m9 13.2 2 2 4-4.2" />
    </Base>
  )
}

/**
 * Auditoría · un escudo con un ojo: se mira todo y nada se toca. Distinto
 * del candado y de la planilla de alistamientos a propósito.
 */
export function IconoAuditoria(p: Props) {
  return (
    <Base {...p}>
      <path d="M12 3 5 6v5.5c0 4.3 3 7.8 7 9.5 4-1.7 7-5.2 7-9.5V6l-7-3Z" />
      <path d="M8.2 12s1.4-2.6 3.8-2.6 3.8 2.6 3.8 2.6-1.4 2.6-3.8 2.6S8.2 12 8.2 12Z" />
      <circle cx="12" cy="12" r="1" />
    </Base>
  )
}

/** Colaboradores · una persona con su credencial. */
export function IconoColaborador(p: Props) {
  return (
    <Base {...p}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M9 3.5h6" />
    </Base>
  )
}

/** Mi trabajo · un portapapeles con lo del día. */
export function IconoMiTrabajo(p: Props) {
  return (
    <Base {...p}>
      <rect x="5" y="4.5" width="14" height="16" rx="1.6" />
      <path d="M9 4.5V3.2A1.2 1.2 0 0 1 10.2 2h3.6A1.2 1.2 0 0 1 15 3.2v1.3Z" />
      <path d="m9 12.5 2 2 4-4" />
    </Base>
  )
}

/** Mis solicitudes · una mano levantada pidiendo. */
export function IconoSolicitudes(p: Props) {
  return (
    <Base {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5l3 2" />
    </Base>
  )
}

/** Avisos · la campana de la barra superior. */
export function IconoCampana(p: Props) {
  return (
    <Base {...p}>
      <path d="M18 8.6a6 6 0 1 0-12 0c0 5.4-2 7-2 7h16s-2-1.6-2-7Z" />
      <path d="M13.7 19a2 2 0 0 1-3.4 0" />
    </Base>
  )
}

/** Lo que se pinta cuando una entrada todavía no tiene icono propio. */
export function IconoPunto(p: Props) {
  return (
    <Base {...p}>
      <circle cx="12" cy="12" r="4" />
    </Base>
  )
}

/**
 * Salir.
 *
 * La puerta con la flecha que sale, que es el dibujo que todo el mundo lee sin
 * pensar. Va en el pie del menú, junto a quién está dentro.
 */
export function IconoSalir(p: Props) {
  return (
    <Base {...p}>
      <path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3" />
      <path d="M15 16l4-4-4-4" />
      <path d="M19 12H10" />
    </Base>
  )
}

/**
 * Cámara.
 *
 * Va donde el operario toma la evidencia. Es lo único que se ofrece ahí: una
 * foto de la galería podría ser de cualquier obra y de cualquier día, y esa
 * evidencia termina en el acta que firma el cliente.
 */
export function IconoCamara(p: Props) {
  return (
    <Base {...p}>
      <path d="M3 8.5h3.2l1.6-2.4h8.4l1.6 2.4H21a1 1 0 0 1 1 1v8.6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="13.6" r="3.5" />
    </Base>
  )
}

/** Colapsar panel lateral hacia la izquierda. */
export function IconoColapsar(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="M15 10l-2 2 2 2" />
    </Base>
  )
}

/** Expandir panel lateral desde la izquierda. */
export function IconoExpandir(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="M13 10l2 2-2 2" />
    </Base>
  )
}

/** Icono sección Operación */
export function IconoOperacion(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </Base>
  )
}

/** Icono sección Comercial */
export function IconoComercial(p: Props) {
  return (
    <Base {...p}>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <circle cx="7" cy="7" r="1.5" />
    </Base>
  )
}

/** Icono sección Financiera */
export function IconoFinanciera(p: Props) {
  return (
    <Base {...p}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <circle cx="16" cy="15" r="1.5" />
    </Base>
  )
}

/** Icono sección Bodega */
export function IconoBodega(p: Props) {
  return (
    <Base {...p}>
      <path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </Base>
  )
}

/** Icono sección Configuración */
export function IconoConfiguracion(p: Props) {
  return (
    <Base {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </Base>
  )
}

/** Icono lápiz para editar */
export function IconoEditar(p: Props) {
  return (
    <Base {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </Base>
  )
}

/** Icono más (+) para añadir */
export function IconoMas(p: Props) {
  return (
    <Base {...p}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </Base>
  )
}

/** Icono Grip / Arrastrar (6 puntos) */
export function IconoGrip(p: Props) {
  return (
    <Base {...p}>
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </Base>
  )
}

/** Icono flecha arriba */
export function IconoFlechaArriba(p: Props) {
  return (
    <Base {...p}>
      <polyline points="18 15 12 9 6 15" />
    </Base>
  )
}

/** Icono flecha abajo */
/** Icono flecha derecha */
export function IconoFlechaDerecha(p: Props) {
  return (
    <Base {...p}>
      <polyline points="9 18 15 12 9 6" />
    </Base>
  )
}

export function IconoFlechaAbajo(p: Props) {
  return (
    <Base {...p}>
      <polyline points="6 9 12 15 18 9" />
    </Base>
  )
}

/** Icono papelera para eliminar */
export function IconoBasura(p: Props) {
  return (
    <Base {...p}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Base>
  )
}

/** Icono check ✓ */
export function IconoCheck(p: Props) {
  return (
    <Base {...p}>
      <polyline points="20 6 9 17 4 12" />
    </Base>
  )
}

/** Icono transferir o mover etapa (flecha hacia carpeta / paso) */
export function IconoMoverEtapa(p: Props) {
  return (
    <Base {...p}>
      <path d="M4 17l6-6-6-6" />
      <path d="M12 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-7" />
    </Base>
  )
}

/** Icono cerrar (X) plano */
export function IconoCerrar(p: Props) {
  return (
    <Base {...p}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Base>
  )
}

/** Icono plano de proyecto */
export function IconoProyecto(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </Base>
  )
}

/** Icono caja / bodega / alistamiento */
export function IconoCaja(p: Props) {
  return (
    <Base {...p}>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </Base>
  )
}

/** Icono lista / detalle de elementos */
export function IconoLista(p: Props) {
  return (
    <Base {...p}>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </Base>
  )
}

/** Icono calendario / fechas */
export function IconoCalendario(p: Props) {
  return (
    <Base {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </Base>
  )
}

/** Icono gráfico / comparador presupuestal */
export function IconoGrafico(p: Props) {
  return (
    <Base {...p}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="2" y1="20" x2="22" y2="20" />
    </Base>
  )
}

/** Icono alerta / advertencia */
export function IconoAlerta(p: Props) {
  return (
    <Base {...p}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Base>
  )
}

/** Icono reloj / pendiente */
export function IconoReloj(p: Props) {
  return (
    <Base {...p}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Base>
  )
}


